import React, { useState } from "react";
import { Button } from "../../../../../components/button";
import { useToast } from "../../../../../components/toast";
import { CompetitionsAPI } from "../../../../../APIs/competitions.api";
import { ConfirmedTeamDataFResults } from "../../../../../types/team.types";
import { EditableField } from "../../../../../components/editable-field";

interface CreateOverallResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  competitionId: number;
  teams: ConfirmedTeamDataFResults[];
  availablePositions: number[];
}

interface FormData {
  team_id: string;
  final_position: string;
  total_points: string;
  total_species: string;
  total_pieces: string;
}

export function CreateOverallResultModal({
  isOpen,
  onClose,
  onSuccess,
  competitionId,
  teams,
  availablePositions,
}: CreateOverallResultModalProps) {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    team_id: "",
    final_position: "",
    total_points: "",
    total_species: "",
    total_pieces: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.team_id || !formData.final_position) {
      addToast({
        type: "error",
        message: "Por favor selecciona un equipo y una posición",
        location: "bottom-center",
      });
      return;
    }

    try {
      setLoading(true);

      const response = await CompetitionsAPI.postCompetitionResult({
        competition_id: competitionId,
        team_id: parseInt(formData.team_id),
        final_position: parseInt(formData.final_position),
        total_points: parseInt(formData.total_points),
        total_species: parseInt(formData.total_species),
        total_pieces: parseInt(formData.total_pieces),
      });

      if (response.statusCode === 201 || response.statusCode === 200) {
        onSuccess();
        resetForm();
      } else {
        throw new Error(response.message || "Error al crear el resultado");
      }
    } catch (error) {
      addToast({
        type: "error",
        message: "Error al crear el resultado",
        location: "bottom-center",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      team_id: "",
      final_position: "",
      total_points: "",
      total_species: "",
      total_pieces: "",
    });
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Agregar Resultado General
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Team Selection */}
          <EditableField
            label="Equipo"
            value={formData.team_id}
            name="team_id"
            type="select"
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Seleccionar equipo"
            options={teams.map((team) => ({
              key: team.id.toString(),
              value: `${team.name}${
                team.institution?.name ? ` - ${team.institution.name}` : ""
              }`,
            }))}
          />
          {teams.length === 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Todos los equipos ya tienen resultados asignados
            </p>
          )}

          {/* Final Position */}
          <EditableField
            label="Posición Final"
            value={formData.final_position}
            name="final_position"
            type="select"
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Seleccionar posición"
            options={availablePositions
              .sort((a, b) => a - b)
              .map((position) => ({
                key: position.toString(),
                value: `${position}°`,
              }))}
          />
          {availablePositions.length === 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Todas las posiciones ya están asignadas
            </p>
          )}

          {/* Total Points */}
          <EditableField
            label="Puntos Totales"
            value={formData.total_points}
            name="total_points"
            type="number"
            onChange={handleInputChange}
            required
            disabled={loading}
            min={0}
            placeholder="Ej: 150"
          />

          {/* Total Species */}
          <EditableField
            label="Total de Especies"
            value={formData.total_species}
            name="total_species"
            type="number"
            onChange={handleInputChange}
            required
            disabled={loading}
            min={0}
            placeholder="Ej: 5"
          />

          {/* Total Pieces */}
          <EditableField
            label="Total de Piezas"
            value={formData.total_pieces}
            name="total_pieces"
            type="number"
            onChange={handleInputChange}
            required
            disabled={loading}
            min={0}
            placeholder="Ej: 12"
          />

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              text="Cancelar"
              onClick={handleClose}
              variant="cancel"
              disabled={loading}
            />
            <Button
              type="submit"
              text={loading ? "Creando..." : "Crear Resultado"}
              variant="create"
              disabled={
                loading || teams.length === 0 || availablePositions.length === 0
              }
            />
          </div>
        </form>
      </div>
    </div>
  );
}
